/**
 * ATS providers come from GET /integrations/connectors, which passes through TalentBridge's
 * connector list. Each one describes the credentials it needs as a small JSON-schema
 * subset, and the connect form is rendered from that rather than hardcoded per provider.
 */

export type LocalizedText = { en?: string; de?: string }

export interface AtsConnectorField {
  type: 'string' | 'number' | 'boolean' | 'array' | 'object'
  /** 'password' | 'url' | 'json' */
  format?: string
  label?: LocalizedText
  enum?: string[]
  default?: unknown
  /** A regex written as "/.../" */
  pattern?: string
  nullable?: boolean
  items?: AtsConnectorSchema
}

export interface AtsConnectorSchema {
  type: 'object'
  properties: Record<string, AtsConnectorField>
  required?: string[]
  displayOrder?: string[]
}

export interface AtsConnector {
  type: string
  name: string
  description?: LocalizedText
  capabilities: string[]
  config: AtsConnectorSchema
  logoUrl?: string
  /** Set by our server: has both applicants:push and jobs:pull. */
  supported: boolean
}

export const REQUIRED_ATS_CAPABILITIES = ['applicants:push', 'jobs:pull'] as const

/** Form state: text inputs hold strings, array fields hold rows of strings. */
export type AtsFormValue = string | boolean | Record<string, string>[]
export type AtsFormValues = Record<string, AtsFormValue>

export type AtsFieldError = 'required' | 'url' | 'pattern' | 'number' | 'json'

export const localize = (text: LocalizedText | undefined, locale: string, fallback: string) =>
  (locale.startsWith('de') ? text?.de : text?.en) || text?.en || fallback

/** Fields in the order TalentBridge asks for, then any it left out of displayOrder. */
export const orderedFields = (schema: AtsConnectorSchema): [string, AtsConnectorField][] => {
  const keys = Object.keys(schema.properties ?? {})
  const order = (schema.displayOrder ?? []).filter((key) => keys.includes(key))
  return [...order, ...keys.filter((key) => !order.includes(key))].map((key) => [
    key,
    schema.properties[key],
  ])
}

export const isRowsField = (field: AtsConnectorField) =>
  field.type === 'array' && field.items?.type === 'object'

/** Free-form structures are entered as JSON text. */
export const isJsonField = (field: AtsConnectorField) =>
  field.format === 'json' ||
  field.type === 'object' ||
  (field.type === 'array' && !isRowsField(field))

const initialValue = (field: AtsConnectorField): AtsFormValue => {
  if (field.type === 'boolean') return field.default === true
  if (isRowsField(field)) return []
  if (field.default === undefined || field.default === null) return ''
  return isJsonField(field) ? JSON.stringify(field.default, null, 2) : String(field.default)
}

export const initialAtsValues = (schema: AtsConnectorSchema): AtsFormValues =>
  Object.fromEntries(orderedFields(schema).map(([key, field]) => [key, initialValue(field)]))

const isEmpty = (value: AtsFormValue | undefined) =>
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0)

const toRegExp = (pattern: string) => {
  const match = pattern.match(/^\/(.*)\/([a-z]*)$/)
  try {
    // The pattern comes from TalentBridge's connector schema, not from user input.
    // eslint-disable-next-line security/detect-non-literal-regexp
    return match ? new RegExp(match[1], match[2]) : new RegExp(pattern)
  } catch {
    return null
  }
}

const isUrl = (value: string) => {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

const validateValue = (
  field: AtsConnectorField,
  value: AtsFormValue | undefined,
  required: boolean
): AtsFieldError | null => {
  if (field.type === 'boolean') return null
  if (isEmpty(value)) return required ? 'required' : null
  if (typeof value !== 'string') return null

  const text = value.trim()
  if (isJsonField(field)) {
    try {
      JSON.parse(text)
      return null
    } catch {
      return 'json'
    }
  }
  if (field.type === 'number') return Number.isFinite(Number(text)) ? null : 'number'
  if (field.format === 'url' && !isUrl(text)) return 'url'
  const regExp = field.pattern ? toRegExp(field.pattern) : null
  if (regExp && !regExp.test(text)) return 'pattern'
  return null
}

/**
 * Field errors keyed by field name. Rows of an array field are keyed `field.index.subfield`.
 * An empty result means the form can be submitted.
 */
export const validateAtsValues = (
  schema: AtsConnectorSchema,
  values: AtsFormValues
): Record<string, AtsFieldError> => {
  const errors: Record<string, AtsFieldError> = {}
  const required = schema.required ?? []

  orderedFields(schema).forEach(([key, field]) => {
    const value = values[key]
    if (isRowsField(field)) {
      if (required.includes(key) && isEmpty(value)) errors[key] = 'required'
      const rows = Array.isArray(value) ? value : []
      rows.forEach((row, index) => {
        Object.entries(validateAtsValues(field.items as AtsConnectorSchema, row)).forEach(
          ([subKey, error]) => {
            errors[`${key}.${index}.${subKey}`] = error
          }
        )
      })
      return
    }
    const error = validateValue(field, value, required.includes(key))
    if (error) errors[key] = error
  })

  return errors
}

/** The config object sent to the server: typed values, empty optional fields left out. */
export const atsValuesToConfig = (
  schema: AtsConnectorSchema,
  values: AtsFormValues
): Record<string, unknown> => {
  const config: Record<string, unknown> = {}

  orderedFields(schema).forEach(([key, field]) => {
    const value = values[key]
    if (field.type === 'boolean') {
      config[key] = value === true
      return
    }
    if (isEmpty(value)) return

    if (Array.isArray(value)) {
      config[key] = value.map((row) => atsValuesToConfig(field.items as AtsConnectorSchema, row))
    } else if (typeof value === 'string') {
      // A password is sent exactly as typed; anything else is trimmed.
      const text = field.format === 'password' ? value : value.trim()
      if (isJsonField(field)) config[key] = JSON.parse(text)
      else if (field.type === 'number') config[key] = Number(text)
      else config[key] = text
    }
  })

  return config
}
